import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { TargetAndTransition } from 'framer-motion';
import { useChatDock, type ChatDockState } from '@/app/providers/ChatDockContext';
import {
  CHAT_DOCK_LAYOUT_ID,
  CHAT_DOCK_SPRING,
  CHAT_DOCK_SETTLE_FALLBACK_MS,
} from '@/common/motion/chatDockMotion';
import './HeaderChatLauncher.css';

const COACHMARK_SEEN_KEY = 'xavior:chat-dock-coachmark-seen';
const COACHMARK_AUTO_DISMISS_MS = 4000;
/** How long the landing effects (pulse ring + sparkles) stay mounted. */
const LANDED_FX_MS = 700;

// In-memory backstop for the once-per-session coachmark when sessionStorage
// is unavailable (e.g. Safari private mode) — at most once per page load.
let coachmarkShownThisLoad = false;

interface HeaderChatLauncherProps {
  /**
   * Fired once when the launcher finishes its docking flight — MainLayout
   * uses it to ripple-nudge the neighboring header icons.
   */
  onLanded?: () => void;
}

/**
 * Header chat launcher — the docked state of the split-view chat panel.
 *
 * Shares `layoutId` with ChatDockFlight inside MainLayout's <LayoutGroup>:
 * when the panel docks, this button mounts as the flight surface unmounts and
 * framer-motion FLIPs it from the chat column rect into the header slot
 * (landing with the spring's slight bounce). Clicking it (or Enter/Space)
 * sends the surface flying back into the panel.
 *
 * Rendered as the FIRST child of `.app-header-actions` — the group is
 * right-aligned, so inserting on the left never moves Demo/Theme/Activities.
 */
export function HeaderChatLauncher({ onLanded }: HeaderChatLauncherProps) {
  const dock = useChatDock();
  return (
    // AnimatePresence snapshots `custom` into the exiting child's presence
    // context at the removal render. Both unmount paths flip dockState in
    // that same render, so the exit variant sees the fresh state (it is NOT
    // live-updated mid-exit — see launcherExit).
    <AnimatePresence custom={dock.dockState} initial={false}>
      {dock.isSplitViewActive && dock.dockState === 'docked' && (
        <LauncherButton key="header-chat-launcher" onLanded={onLanded} />
      )}
    </AnimatePresence>
  );
}

/**
 * Exit variant for the launcher's two unmount paths:
 * - 'undocking' → the unmount IS the flight handoff (ChatDockFlight mounts in
 *   the same commit and FLIPs from this slot) — leave instantly, no effects.
 * - anything else ('open': module closed under a docked chat, programmatic
 *   expandChatPane) → there is no flight, so "poof": a quick scale-up fade
 *   that makes it clear the icon is intentionally leaving.
 * Exported for tests only (the framer mock strips exit animations, so the
 * dispatcher logic is unit-tested directly).
 */
export function launcherExit(reducedMotion: boolean) {
  return (dockState: ChatDockState): TargetAndTransition => {
    if (dockState === 'undocking') {
      // Invisible from the very first hand-off frame: framer keeps the
      // exiting button alive briefly for the shared-layoutId transfer and
      // projects it alongside the growing flight — at opacity 1 its glyph
      // shows up as a giant icon inside the expanding container.
      return { opacity: 0, transition: { duration: 0 } };
    }
    if (reducedMotion) {
      return { opacity: 0, transition: { duration: 0.12, ease: 'easeOut' } };
    }
    return {
      opacity: 0,
      scale: 1.6,
      filter: 'blur(2px)',
      transition: { duration: 0.18, ease: 'easeOut' },
    };
  };
}

function LauncherButton({ onLanded }: HeaderChatLauncherProps) {
  const { pendingLandingFx, ackLandingFx, undockChat } = useChatDock();
  const prefersReducedMotion = useReducedMotion() ?? false;
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // 'flying'  = traveling from the panel (wears the panel surface, icon hidden)
  // 'landed'  = touchdown effects running (pulse ring, sparkles)
  // 'idle'    = plain header icon button
  // 'handoff' = undock click committed, glyph hidden — see handleClick
  const [phase, setPhase] = useState<'flying' | 'landed' | 'idle' | 'handoff'>(() =>
    pendingLandingFx && !prefersReducedMotion ? 'flying' : 'idle',
  );
  const [showCoachmark, setShowCoachmark] = useState(false);
  const landedRef = useRef(false);

  const handleLanded = useCallback(() => {
    if (landedRef.current) return;
    landedRef.current = true;
    ackLandingFx();
    // Spec: closing the panel moves focus to the launcher once it lands.
    buttonRef.current?.focus();
    if (prefersReducedMotion) {
      setPhase('idle');
    } else {
      setPhase('landed');
      onLanded?.();
    }
    // First user-initiated dock per session: point at the new home. The
    // in-memory flag only governs when sessionStorage is unavailable
    // (Safari private mode) — then it's at most once per page load.
    let seen: boolean;
    try {
      seen = window.sessionStorage.getItem(COACHMARK_SEEN_KEY) === '1';
      window.sessionStorage.setItem(COACHMARK_SEEN_KEY, '1');
    } catch {
      seen = coachmarkShownThisLoad;
    }
    if (!seen) {
      coachmarkShownThisLoad = true;
      setShowCoachmark(true);
    }
  }, [ackLandingFx, onLanded, prefersReducedMotion]);

  // Landing driver. With motion enabled, onLayoutAnimationComplete is the
  // primary signal and the timeout is the can't-wedge fallback; with reduced
  // motion (or a restored mount that still has the flag) land immediately.
  useEffect(() => {
    if (!pendingLandingFx || landedRef.current) return;
    if (phase === 'flying') {
      const t = window.setTimeout(handleLanded, CHAT_DOCK_SETTLE_FALLBACK_MS);
      return () => window.clearTimeout(t);
    }
    handleLanded();
  }, [pendingLandingFx, phase, handleLanded]);

  // Retire the touchdown effects.
  useEffect(() => {
    if (phase !== 'landed') return;
    const t = window.setTimeout(() => setPhase('idle'), LANDED_FX_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  // Coachmark auto-dismiss.
  useEffect(() => {
    if (!showCoachmark) return;
    const t = window.setTimeout(() => setShowCoachmark(false), COACHMARK_AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [showCoachmark]);

  const handleClick = () => {
    setShowCoachmark(false);
    // Hide the glyph in its own SYNCHRONOUS commit before the unmount:
    // the exiting button shares the morph layoutId, so framer keeps it alive
    // for the whole flight and projects it along the path — AnimatePresence
    // freezes its LAST committed render, and a visible glyph in that snapshot
    // rides the expansion as a ghost icon (the crossfade system also owns
    // opacity for shared elements, so an exit-variant opacity can't kill it).
    flushSync(() => setPhase('handoff'));
    undockChat();
  };

  return (
    <span className="header-chat-launcher-wrap">
      <motion.button
        ref={buttonRef}
        type="button"
        className={`app-header-activities header-chat-launcher${
          phase === 'flying' ? ' is-flying' : ''
        }${phase === 'landed' ? ' is-landed' : ''}${
          phase === 'handoff' ? ' is-handoff' : ''
        }`}
        title="Open chat"
        aria-label="Open chat"
        aria-expanded={false}
        aria-controls="chat-dock-panel"
        onClick={handleClick}
        // Reduced motion: no shared-element flight — just a quick fade.
        layoutId={prefersReducedMotion ? undefined : CHAT_DOCK_LAYOUT_ID}
        transition={prefersReducedMotion ? { duration: 0.15 } : CHAT_DOCK_SPRING}
        initial={prefersReducedMotion ? { opacity: 0 } : false}
        animate={prefersReducedMotion ? { opacity: 1 } : undefined}
        variants={{ exit: launcherExit(prefersReducedMotion) }}
        exit="exit"
        onLayoutAnimationComplete={phase === 'flying' ? handleLanded : undefined}
      >
        {/* Glass for the flight: WebKit can drop backdrop-filter when it
            shares an element with mask-image, so the mask sits on the button
            and the blur on this surface span (styled only while flying). */}
        <span className="header-chat-launcher-surface" aria-hidden="true" />
        <svg
          className="header-chat-launcher-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
        </svg>
        <span className="header-chat-launcher-pulse" aria-hidden="true" />
        <span className="header-chat-launcher-sparkles" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </motion.button>
      {showCoachmark && (
        <span className="header-chat-launcher-coachmark" role="status">
          Chat moved here
        </span>
      )}
    </span>
  );
}
